import Lottie from 'lottie-react';

export interface LottieAnimationProps {
  animationData: unknown;
  loop?: boolean;
  /**
   * CSS width of the wrapper div. Use a pixel number for fixed size (e.g. 277)
   * or a CSS string for responsive sizing (e.g. 'clamp(160px, 18vw, 360px)').
   */
  width: number | string;
  /**
   * Aspect ratio expressed as 'W / H' (e.g. '432 / 350', '1 / 1').
   * Height is derived from this so no viewport units are ever placed on the
   * height axis — which prevents Firefox's SVG renderer from inflating.
   */
  aspectRatio: string;
  /** Defaults to '100%'. Override only when the caller needs a tighter cap. */
  maxWidth?: number | string;
}

/**
 * Firefox-safe Lottie wrapper for display/decorative animations.
 *
 * Firefox inflates Lottie SVGs when both width and height are derived from
 * viewport or percentage units. This component avoids that by:
 *  - letting the caller control width (fixed px or clamp)
 *  - deriving height solely from `aspect-ratio` (no vw/% on the height axis)
 *  - passing `width: 100%; height: 100%` to Lottie so it reads concrete pixel
 *    values from the wrapper's clientWidth / clientHeight
 */
export const LottieAnimation: React.FC<LottieAnimationProps> = ({
  animationData,
  loop = false,
  width,
  aspectRatio,
  maxWidth = '100%',
}) => (
  <div style={{ width, maxWidth, aspectRatio }}>
    <Lottie
      animationData={animationData}
      loop={loop}
      rendererSettings={{ preserveAspectRatio: 'xMidYMid meet' }}
      style={{ width: '100%', height: '100%' }}
    />
  </div>
);
